import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Icons from 'react-icons/fa';
import CustomerHeader from '../components/CustomerHeader';
import CustomAlert from '../components/CustomAlert';
import DynamicFormRenderer from '../components/DynamicFormRenderer';
import LoadingSpinner from '../components/LoadingSpinner';
import RevertModal from '../components/RevertModal';
import OTPValidationModal from '../components/OTPValidationModal';
import formConfigService, { FormConfiguration, FormField } from '../services/formConfigService';
import webApiFormService, { FORM_NAMES, FORM_TYPES } from '../services/webApiFormService';
import equipmentService from '../services/equipmentService';
import dynamicEntityService from '../services/dynamicEntityService';
import apiService from '../services/apiService';

const FaLock = Icons.FaLock as any;

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0;
    gap: 0.5rem;
  }
`;

const FormNumberDisplay = styled.div`
  text-align: right;
  padding: 0.5rem 1rem;
  margin-top: 1rem;
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
  font-family: monospace;
  opacity: 0.7;

  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.7rem;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;

  ${props => props.variant === 'secondary' ? `
    background: white;
    color: ${props.theme.textPrimary};
    border: 2px solid ${props.theme.border};

    &:hover:not(:disabled) {
      background: ${props.theme.surface};
    }
  ` : `
    background: ${props.theme.primary};
    color: white;

    &:hover:not(:disabled) {
      background: ${props.theme.primaryDark};
      transform: translateY(-2px);
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1rem;
`;

const LoadingText = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 1rem;
`;

const NoAccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 250px;
  padding: 2rem 1rem;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin: 2rem auto;
  max-width: 400px;
`;

const NoAccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  
  svg {
    color: white;
    font-size: 1.5rem;
  }
`;

const NoAccessTitle = styled.h2`
  color: ${props => props.theme.textPrimary};
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const NoAccessMessage = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  margin-bottom: 1.5rem;
  line-height: 1.4;
  max-width: 280px;
`;

const BackButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  
  &:hover {
    background: ${props => props.theme.primaryDark};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Convert Web API response to FormConfiguration format
const convertWebApiResponseToFormConfig = async (webApiResponse: any): Promise<FormConfiguration> => {
  const fields: FormField[] = webApiResponse.data.map((field: any) => {
    // The Web API service already mapped the fields, so we use the mapped names
    let fieldType = field.inputType || 'text';

    // Map UI_Input_Type values to our field types
    const inputTypeMap: { [key: string]: string } = {
      'Text': 'text',
      'TextBox': 'text',
      'TextArea': 'textarea',
      'Number': 'number',
      'Email': 'email',
      'Date': 'date',
      'DateTime': 'datetime-local',
      'DropDown': 'dropdown',
      'Dropdown': 'dropdown',
      'MultiSelect': 'multiselect',
      'CheckBox': 'checkbox',
      'RadioButton': 'radio',
      'FileUpload': 'fileupload',
      'Image': 'fileupload',
      'Camera': 'camera',
      'Location': 'location',
      'GeoLocationCapture': 'location',
      'Scanner': 'qrscanner',
      'BarcodeScanner': 'qrscanner',
      'QRScanner': 'qrscanner',
      'Barcode Scanner': 'qrscanner',
      'QR Scanner': 'qrscanner',
      'SignaturePad': 'signaturepad',
      'AgreementTemplate': 'agreementtemplate',
      'TextBoxWithSearch': 'textboxwithsearch',
      'H2': 'h2',
      'P': 'paragraph',
      'Paragraph': 'paragraph'
    };

    // Use the mapped input type from the database
    // Do NOT auto-detect field types by name to avoid false positives
    // Always trust the UI_Input_Type from the database configuration
    if (inputTypeMap[field.inputType]) {
      fieldType = inputTypeMap[field.inputType];
    }

    // Check if this is a display-only field (paragraph or heading)
    const isDisplayOnlyField = fieldType === 'paragraph' || fieldType === 'h2';

    // Use Table_Column_Name which has the actual database column names
    // For display-only fields, use fieldId as the field name since they don't have a column
    const tableColumnName = field.Table_Column_Name || field.tableColumnName || field.columnName || '';

    // CRITICAL: Skip fields where column name contains SQL data types or is invalid
    // But allow display-only fields (they don't need a column name)
    const sqlDataTypes = ['nvarchar', 'varchar', 'int', 'decimal', 'datetime', 'bit', 'float', 'char'];
    const columnNameLower = tableColumnName.toLowerCase();
    if (!isDisplayOnlyField && sqlDataTypes.some(dt => columnNameLower.includes(dt) && columnNameLower.includes('('))) {
      console.warn(`SKIPPING FIELD: Column name contains SQL data type: "${tableColumnName}"`);
      return null; // Return null to filter out this field
    }

    // If Table_Column_Name is empty or invalid, skip this field
    // But allow display-only fields (paragraph, h2) - they use fieldId instead
    if (!isDisplayOnlyField && (!tableColumnName || tableColumnName.trim() === '')) {
      console.warn(`SKIPPING FIELD: No valid Table_Column_Name found for field: ${field.fieldText}`);
      return null;
    }

    // Map Web API field format to FormField format
    // Use Table_Column_Name as the field name (form control key)
    // For display-only fields (paragraph, h2), use fieldId since they don't have a column
    const formField: FormField = {
      fieldName: isDisplayOnlyField ? (field.fieldId || field.UI_Field_Id || `display_${field.fieldOrder || 0}`) : tableColumnName,
      label: field.fieldText || field.columnName || field.fieldId,
      fieldType: fieldType, // Already mapped above
      isVisible: field.isVisible !== false,
      isMandatory: field.isMandatory === true,
      isReadOnly: false,
      isEditable: field.isEnabled !== false,
      displayOrder: field.fieldOrder || 0,
      section: field.mainTab || 'General',
      columnSpan: 1,
      // For paragraph fields, use the raw text without "Enter" prefix
      placeholder: fieldType === 'paragraph' ? (field.fieldText || '') : `Enter ${field.fieldText || field.columnName}`,
      helpText: field.helpText || '',
      // For 'backend' inputValue, don't set a default - let backend generate the value
      defaultValue: field.inputValue && field.inputValue.toLowerCase() !== 'backend' ? field.inputValue : '',

      // CRITICAL: Copy TextBoxWithSearch API endpoint properties
      onChangeApiEndpoint1: field.onChangeApiEndpoint1,
      onChangeApiEndpoint2: field.onChangeApiEndpoint2,
      onChangeApiEndpoint3: field.onChangeApiEndpoint3,
      onChangeApiEndpoint4: field.onChangeApiEndpoint4,
      onChangeApiEndpoint5: field.onChangeApiEndpoint5,
      onChangeApiCount: field.onChangeApiCount,
      isOnChangeFetchDataFromAPI: field.isOnChangeFetchDataFromAPI,

      // Auto-population configuration
      isApiPopulatedColumn: field.isApiPopulatedColumn,
      apiPopulatedColumnParentFieldId: field.apiPopulatedColumnParentFieldId,
      apiPopulatedColumnParentFieldIdPropertyName: field.apiPopulatedColumnParentFieldIdPropertyName,
      apiNumber: field.apiNumber,

      // Parent field visibility configuration
      parentFieldId: field.parentFieldId,
      parentFieldValue: field.parentFieldValue,
      conditionOperator: field.conditionOperator,
      conditionalLogicOperator: field.conditionalLogicOperator,
      secondaryParentFieldId: field.secondaryParentFieldId,
      secondaryParentFieldValue: field.secondaryParentFieldValue,

      // Repeating Section Configuration
      isRepeatingSection: field.isRepeatingSection,
      repeatingSectionId: field.repeatingSectionId,
      repeatingSectionName: field.repeatingSectionName,
      repeatingTriggerType: field.repeatingTriggerType,
      repeatingTriggerFieldId: field.repeatingTriggerFieldId,
      repeatingSectionApiEndpoint: field.repeatingSectionApiEndpoint,
      repeatingMinRows: field.repeatingMinRows,
      repeatingMaxRows: field.repeatingMaxRows,
      repeatingAllowAddRemove: field.repeatingAllowAddRemove,
      repeatingLayout: field.repeatingLayout,
      repeatingShowRowNumbers: field.repeatingShowRowNumbers,
      repeatingShowDeleteButton: field.repeatingShowDeleteButton,
      repeatingRowTitleTemplate: field.repeatingRowTitleTemplate,
      repeatingParentSectionId: field.repeatingParentSectionId,
      repeatingChildSectionIds: field.repeatingChildSectionIds,
      repeatingNestedLevel: field.repeatingNestedLevel,
      repeatingAllowNesting: field.repeatingAllowNesting,

      // Table metadata
      tableName: field.tableName,
      tableColumnName: field.Table_Column_Name || field.tableColumnName || field.columnName,
      tableType: field.tableType
    };

    // Handle dropdown fields
    if (fieldType === 'dropdown' || fieldType === 'multiselect') {
      // The webApiFormService already mapped these fields
      const sourceType = field.optionsSource || 'static';
      formField.sourceType = sourceType.toLowerCase();

      // Handle static options - comes as staticOptions from webApiFormService
      const staticValues = field.staticOptions;
      if (staticValues) {
        try {
          if (typeof staticValues === 'string') {
            // Parse JSON string
            const parsed = JSON.parse(staticValues);
            // Convert to FieldOption format if needed
            if (Array.isArray(parsed)) {
              formField.staticOptions = parsed.map((opt: any) => {
                if (typeof opt === 'string') {
                  return { value: opt, text: opt };
                } else if (opt.value && opt.text) {
                  return { value: opt.value, text: opt.text };
                } else if (opt.value && opt.label) {
                  return { value: opt.value, text: opt.label };
                } else if (opt.Value && opt.Label) {
                  return { value: opt.Value, text: opt.Label };
                } else if (opt.code && opt.name) {
                  return { value: opt.code, text: opt.name };
                }
                return { value: String(opt), text: String(opt) };
              });
            } else {
              formField.staticOptions = [];
            }
          } else if (Array.isArray(staticValues)) {
            formField.staticOptions = staticValues;
          } else {
            formField.staticOptions = [];
          }
        } catch (e) {
          console.error('Error parsing static options:', e, staticValues);
          formField.staticOptions = [];
        }
      }

      // Handle API endpoint - already mapped as apiEndpoint by webApiFormService
      // Support both 'api' and 'indexeddb' source types
      if (formField.sourceType === 'api' || formField.sourceType === 'indexeddb') {
        const apiEndpoint = field.apiEndpoint;

        if (apiEndpoint) {
          // Store the endpoint as-is, we'll prepend the Web API URL when fetching
          formField.dataSource = apiEndpoint;
        } else {
          console.warn(`Field ${field.columnName} has source type '${formField.sourceType}' but no API endpoint`);
          formField.sourceType = 'static'; // Fallback to static if no endpoint
        }
      }
    }

    // Handle validation rules
    if (field.validationRules) {
      try {
        const rules = typeof field.validationRules === 'string'
          ? JSON.parse(field.validationRules)
          : field.validationRules;

        if (rules.pattern) formField.validationPattern = rules.pattern;
        // Convert minLength/maxLength to numbers (they may come as strings from JSON)
        if (rules.minLength !== undefined) formField.minLength = parseInt(String(rules.minLength), 10);
        if (rules.maxLength !== undefined) formField.maxLength = parseInt(String(rules.maxLength), 10);
        if (rules.minValue !== undefined) formField.minValue = parseFloat(String(rules.minValue));
        if (rules.maxValue !== undefined) formField.maxValue = parseFloat(String(rules.maxValue));
        if (rules.errorMessage) formField.errorMessage = rules.errorMessage;
        // Handle required from validation rules (in addition to isMandatory)
        if (rules.required === true) formField.isMandatory = true;
        // Handle email validation rule
        if (rules.email === true) formField.isEmail = true;
      } catch (e) {
        console.warn('Error parsing validation rules:', e);
      }
    }

    return formField;
  }).filter((field: FormField | null) => field !== null) as FormField[];  // Filter out null values from invalid fields

  // Sort by display order
  fields.sort((a, b) => a.displayOrder - b.displayOrder);

  // Group fields by section - only include visible fields
  const sections = new Map<string, FormField[]>();
  const visibleFields: FormField[] = [];

  fields.forEach(field => {
    // Only group visible fields into sections
    if (field.isVisible !== false) {
      const section = field.section || 'General';
      if (!sections.has(section)) {
        sections.set(section, []);
      }
      sections.get(section)!.push(field);
      visibleFields.push(field);
    }
  });

  return { fields, sections };
};


const DynamicServiceRequest: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { formName: urlFormName } = useParams<{ formName: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formConfig, setFormConfig] = useState<FormConfiguration | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentFormType, setCurrentFormType] = useState<string>('');
  const [currentFormName, setCurrentFormName] = useState<string>('');
  const [repeatingSectionData, setRepeatingSectionData] = useState<Record<string, any[]>>({});
  const [hasAccess, setHasAccess] = useState<boolean>(true);
  const [mode, setMode] = useState<'add' | 'edit' | 'view'>('add');
  const [recordUid, setRecordUid] = useState<string>('');
  const [nextStatus, setNextStatus] = useState<string>('Confirm');
  const [initialNextStatus, setInitialNextStatus] = useState<string>('');
  const [canRevert, setCanRevert] = useState<boolean>(false);
  const [isRevertModalOpen, setIsRevertModalOpen] = useState<boolean>(false);
  const [formHistory, setFormHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [approvalMatrixLineUID, setApprovalMatrixLineUID] = useState<string>(''); // Store original approval matrix UID
  const [showOTPModal, setShowOTPModal] = useState<boolean>(false);
  const [otpValidated, setOtpValidated] = useState<boolean>(false);
  const [validatedPhoneNumber, setValidatedPhoneNumber] = useState<string>('');
  const [pendingSubmissionData, setPendingSubmissionData] = useState<any>(null);
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
    onClose?: () => void;
  }>({ isVisible: false, message: '' });

  // Determine form type from URL parameters and pathname
  const getFormTypeFromParams = () => {
    const type = searchParams.get('type');
    const pathname = location.pathname;

    // Check for dynamic form route with formName parameter
    if (urlFormName) {
      return { formName: urlFormName, formType: 'DYNAMIC', title: urlFormName };
    }

    // Check URL path first
    if (pathname.includes('/cde-verification') || type === 'VERIFICATION') {
      return { formName: FORM_NAMES.VERIFICATION, formType: 'VERIFICATION', title: 'CDE Verification', isPublic: true };
    } else if (pathname.includes('/pullout-request') || type === 'PULLOUT_REQUEST') {
      return { formName: FORM_NAMES.PULLOUT_REQUEST, formType: FORM_TYPES.PULLOUT_REQUEST, title: 'CDE Removal Request' };
    } else if (pathname.includes('/water-sampling') || type === 'WATER_SAMPLING') {
      return { formName: FORM_NAMES.WATER_SAMPLING, formType: FORM_TYPES.WATER_SAMPLING, title: 'Water Sampling Request' };
    } else if (pathname.includes('/unit-relocation') || type === 'RELOCATION') {
      return { formName: FORM_NAMES.RELOCATION, formType: FORM_TYPES.RELOCATION, title: 'CDE Relocation Request' };
    } else if (pathname.includes('/complaints') || type === 'COMPLAINT') {
      return { formName: FORM_NAMES.COMPLAINT, formType: FORM_TYPES.COMPLAINT, title: 'Complaint' };
    } else if (pathname.includes('/raise-service-request') || type === 'SERVICE_REQUEST') {
      return { formName: FORM_NAMES.CDE_SERVICE, formType: FORM_TYPES.CDE_SERVICE, title: 'Service Request' };
    } else {
      // Default to CDE Service
      return { formName: FORM_NAMES.CDE_SERVICE, formType: FORM_TYPES.CDE_SERVICE, title: 'CDE Service Request' };
    }
  };

  // Load form configuration on mount and when form type changes
  useEffect(() => {
    const formInfo = getFormTypeFromParams();
    setCurrentFormType(formInfo.formType);
    setCurrentFormName(formInfo.formName);

    // Reset OTP validation state when form changes or page loads
    setOtpValidated(false);
    setValidatedPhoneNumber('');
    setShowOTPModal(false);
    setPendingSubmissionData(null);

    // Check for edit or view mode
    const uid = searchParams.get('uid');
    const modeParam = searchParams.get('mode')?.toUpperCase();

    if (uid && (modeParam === 'EDIT' || modeParam === 'VIEW')) {
      const formMode = modeParam.toLowerCase() as 'edit' | 'view';
      setMode(formMode);
      setRecordUid(uid);
      loadFormConfigurationAndData(formInfo.formName, uid, formMode);
    } else {
      setMode('add');
      setRecordUid('');
      loadFormConfiguration(formInfo.formName);
    }
    // Customer data will be loaded after form configuration
  }, [searchParams, location.pathname]);

  const loadFormConfigurationAndData = async (formName: string, uid: string, formMode: 'edit' | 'view') => {
    setIsLoading(true);
    try {
      // For verification form (public registration), bypass access check
      const isPublicForm = formName === 'CDE Verification using Serial Number';
      
      // Get current status from URL (only needed for EDIT mode, not VIEW)
      const currentStatus = formMode === 'view' ? '' : (searchParams.get('status') || '');
      
      // Load form configuration first (use EDIT/VIEW mode)
      const configResponse = await webApiFormService.getFormConfiguration(
        formName, 
        formMode === 'view' ? 'VIEW' : 'EDIT', 
        currentStatus
      );
      
      if (!configResponse.success) {
        throw new Error(configResponse.message || 'Failed to get form configuration');
      }
      
      // In VIEW mode, we don't need canCreate or canEdit permissions
      if (formMode !== 'view' && !configResponse.canCreate && !configResponse.canEdit && !isPublicForm) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }
      
      const formConfig = await convertWebApiResponseToFormConfig(configResponse);
      
      // If in VIEW mode, make all fields read-only
      if (formMode === 'view') {
        formConfig.fields = formConfig.fields.map(field => ({
          ...field,
          isReadOnly: true,
          isEditable: false
        }));
      }
      
      setFormConfig(formConfig);

      if (configResponse.nextStatus) {
        setInitialNextStatus(configResponse.nextStatus);
        if (formMode !== 'view') {
          setNextStatus(configResponse.nextStatus);
        }
      }
      
      // Store the ApprovalMatrixLineUID from the current status (for revert)
      if (configResponse.approvalMatrixLineUID) {
        setApprovalMatrixLineUID(configResponse.approvalMatrixLineUID);
        console.log('✅ Stored ApprovalMatrixLineUID from current status:', configResponse.approvalMatrixLineUID);
      }
      
      // Set revert permission (only for EDIT mode)
      if (formMode === 'edit' && configResponse.canRevert) {
        setCanRevert(true);
      }
      
      if (isPublicForm) {
        setHasAccess(true);
      }
      
      // Load existing form data
      const dataResponse = await webApiFormService.getFormData(formName, uid);
      
      if (dataResponse.success && dataResponse.data) {
        // Bind data to form
        bindDataToForm(dataResponse.data, formConfig.fields);
      } else {
        throw new Error('Failed to load form data');
      }
      
    } catch (error: any) {
      console.error('Error loading form:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Load Error',
        message: error.message || 'Failed to load form. Please try again.',
        onConfirm: () => navigate('/dashboard')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const bindDataToForm = (loadedData: any, fields: FormField[]) => {
    console.log('🔄 bindDataToForm called - Edit mode data binding');
    console.log('📋 Loaded data tables:', Object.keys(loadedData));

    const boundData: any = {};

    // Process all tables in response
    Object.keys(loadedData).forEach(tableName => {
      const tableData = loadedData[tableName];
      
      // Handle header tables (single object)
      if (!Array.isArray(tableData) && typeof tableData === 'object') {
        Object.assign(boundData, tableData);
      }
    });
    
    // Convert date fields and other special types to input format
    fields.forEach(field => {
      if (boundData[field.fieldName] !== undefined && boundData[field.fieldName] !== null) {
        // Handle date/datetime fields - check what the actual HTML input type will be
        if (field.fieldType === 'date' || field.fieldType === 'datetime' || field.fieldType === 'datetime-local') {
          const date = new Date(boundData[field.fieldName]);
          if (!isNaN(date.getTime())) {
            // 'datetime' fieldType renders as 'datetime-local' input
            if (field.fieldType === 'datetime' || field.fieldType === 'datetime-local') {
              // Format: YYYY-MM-DDTHH:MM for datetime-local input
              boundData[field.fieldName] = date.toISOString().slice(0, 16);
            } else {
              // Format: YYYY-MM-DD for date input
              boundData[field.fieldName] = date.toISOString().split('T')[0];
            }
          }
        } else if (field.fieldType === 'checkbox' || field.fieldType === 'agreementtemplate') {
          boundData[field.fieldName] = boundData[field.fieldName] === true || 
                                        boundData[field.fieldName] === '1' || 
                                        boundData[field.fieldName] === 1;
        } else if (field.fieldType === 'multiselect') {
          // Ensure multiselect values are arrays
          const val = boundData[field.fieldName];
          if (typeof val === 'string') {
            // If it's a JSON string, parse it
            try {
              boundData[field.fieldName] = JSON.parse(val);
            } catch {
              // If it's a comma-separated string, split it
              boundData[field.fieldName] = val.split(',').map((v: string) => v.trim());
            }
          } else if (!Array.isArray(val)) {
            // Convert single value to array
            boundData[field.fieldName] = val ? [val] : [];
          }
        } else if (field.fieldType === 'fileupload' || field.fieldType === 'camera') {
          // Handle image/file fields - add base URL if it's a relative path
          const val = boundData[field.fieldName];
          if (val && typeof val === 'string') {
            // If it's not already a data URL or full URL, prepend base URL
            if (!val.startsWith('data:') && !val.startsWith('http://') && !val.startsWith('https://')) {
              const baseUrl = 'https://cocacola-uat-web.winitsoftware.com'; // Use your API base URL
              boundData[field.fieldName] = `${baseUrl}${val.startsWith('/') ? '' : '/'}${val}`;
            }
          }
        }
      }
    });
    
    // Set form data - this will trigger dependent dropdowns to load via useEffect in DynamicFormRenderer
    setFormData(boundData);

    console.log('⏰ Setting timeout to trigger onChange APIs in 500ms...');
    console.log('📦 Bound data to be checked:', Object.keys(boundData).slice(0, 10));
    console.log('📝 Fields count:', fields.length);

    // Trigger onChange APIs for customer number and serial number fields after form is loaded
    setTimeout(() => {
      console.log('⏰ Timeout executed - calling triggerOnChangeAPIsAfterLoad');
      triggerOnChangeAPIsAfterLoad(boundData, fields);
    }, 500);
  };

  const triggerOnChangeAPIsAfterLoad = (boundData: any, fields: FormField[]) => {
    console.log('🔄 Checking fields for auto-trigger after load...');
    
    const isRaiseServiceRequest = location.pathname === '/raise-service-request';

    // Find fields that have {search_value} or {value} in their onChange API endpoints
    const fieldsToTrigger = fields.filter(field => {
      const hasOnChangeApi = field.onChangeApiEndpoint1 && field.onChangeApiEndpoint1.trim() !== '';
      const hasValue = boundData[field.fieldName] && boundData[field.fieldName] !== '';

      // Check if the API endpoint contains search placeholders
      const endpoint = (field.onChangeApiEndpoint1 || '').toLowerCase();
      const hasSearchPlaceholder = endpoint.includes('{search_value}') || endpoint.includes('{value}');

      // For service requests, we specifically want to trigger serial number fields even if they don't strictly follow the placeholder rule
      const fieldNameLower = field.fieldName.toLowerCase();
      const isSerialField = fieldNameLower.includes('serial') || fieldNameLower === 'serial_no_scanner' || fieldNameLower === 'cdesc_serial_no';
      
      if (hasOnChangeApi) {
        console.log(`🔍 Field: ${field.fieldName}, hasValue: ${hasValue}, hasSearchPlaceholder: ${hasSearchPlaceholder}, isSerial: ${isSerialField}`);
      }

      return hasOnChangeApi && hasValue && (hasSearchPlaceholder || (isRaiseServiceRequest && isSerialField));
    });

    console.log(`✅ Found ${fieldsToTrigger.length} fields to auto-trigger:`, fieldsToTrigger.map(f => f.fieldName));

    // Trigger each field's onChange API
    fieldsToTrigger.forEach(field => {
      const value = boundData[field.fieldName];
      console.log(`🚀 Triggering onChange for ${field.fieldName} with value: ${value}`);
      // Dispatch a custom event that DynamicFormRenderer will listen to
      window.dispatchEvent(new CustomEvent('triggerFieldOnChange', {
        detail: { fieldName: field.fieldName, value }
      }));
    });
  };

  const triggerDependentFieldSearches = async (formData: any, fields: FormField[]) => {
    try {
      // Find fields that have dependent field configurations
      const fieldsWithDependencies = fields.filter(field => 
        field.isOnChangeFetchDataFromAPI && 
        field.onChangeApiCount && 
        field.onChangeApiCount > 0
      );
      
      // console.log('Fields with dependent field configurations:', fieldsWithDependencies.map(f => ({
      //   fieldName: f.fieldName,
      //   label: f.label,
      //   onChangeApiCount: f.onChangeApiCount
      // })));
      
      // Find fields that depend on customer data
      const customerDependentFields = fields.filter(field => 
        field.apiPopulatedColumnParentFieldId && 
        formData[field.apiPopulatedColumnParentFieldId]
      );
      
      // console.log('Customer-dependent fields found:', customerDependentFields.map(f => ({
      //   fieldName: f.fieldName,
      //   label: f.label,
      //   parentField: f.apiPopulatedColumnParentFieldId,
      //   parentValue: f.apiPopulatedColumnParentFieldId ? formData[f.apiPopulatedColumnParentFieldId] : undefined
      // })));
      
      // Also check for fields that have onChange API endpoints and are populated
      const onChangeFields = fields.filter(field => 
        field.isOnChangeFetchDataFromAPI && 
        field.onChangeApiCount && 
        field.onChangeApiCount > 0 &&
        formData[field.fieldName]
      );
      
      // console.log('Fields with onChange API and populated values:', onChangeFields.map(f => ({
      //   fieldName: f.fieldName,
      //   label: f.label,
      //   value: formData[f.fieldName],
      //   onChangeApiCount: f.onChangeApiCount
      // })));
      
      // Trigger dependent field loading by simulating form data changes
      // This will cause the DynamicFormRenderer useEffect to detect changes and load dependent fields
      const fieldsToTrigger = [...fieldsWithDependencies, ...customerDependentFields, ...onChangeFields];
      
      if (fieldsToTrigger.length > 0) {
        // Use a small delay to ensure the form data is fully updated
        setTimeout(() => {
          // Force a re-render by updating form data with a timestamp
          setFormData((prevData: any) => ({
            ...prevData,
            _dependentFieldTrigger: Date.now()
          }));
          
          // Remove the trigger after a short delay
          setTimeout(() => {
            setFormData((prevData: any) => {
              const { _dependentFieldTrigger: _, ...rest } = prevData;
              return rest;
            });
          }, 200);
        }, 100);
      }
      
    } catch (error) {
      console.error('Error triggering dependent field searches:', error);
    }
  };

  const autoBindScannerAndCustomerData = async (initialData: any, fields: FormField[]) => {
    try {
      // Check for serial number from scanner (stored in localStorage from home screen)
      const scannedSerialNumber = localStorage.getItem('serialNumber');
      
      // Check for CDE unit data from navigation state (from verify-cde page)
      const navigationState = location.state;
      const cdeUnit = navigationState?.cdeUnit;

      // Auto-populate scanner fields with scanned serial number
      if (scannedSerialNumber) {
        fields.forEach(field => {
          // Skip fields the current approval-matrix step has marked invisible —
          // same rationale as the loadCustomerData gate further down.
          if (field.isVisible === false) return;

          const fieldName = field.fieldName?.toLowerCase();

          // Comprehensive pattern matching for serial number fields
          const isSerialField = fieldName && (
            // Scanner fields
            fieldName.includes('scanner') ||
            // Serial number variations
            fieldName.includes('serial') ||
            fieldName.includes('serial_no') ||
            fieldName.includes('serialnumber') ||
            fieldName.includes('serial_number') ||
            fieldName.includes('serialno') ||
            // Manufacturer serial number variations
            fieldName.includes('manuf') ||
            fieldName.includes('manufacturer') ||
            // Equipment serial variations
            fieldName.includes('equipment_serial') ||
            fieldName.includes('equipmentserial')
          );
          
          if (isSerialField) {
            initialData[field.fieldName] = scannedSerialNumber;
          }
        });
      }
      
      // Auto-populate fields with CDE unit data from navigation state
      if (cdeUnit) {
        fields.forEach(field => {
          // Skip fields the current approval-matrix step has marked invisible.
          if (field.isVisible === false) return;

          const fieldName = field.fieldName?.toLowerCase();

          // Map CDE unit properties to form fields
          // Check for serial number fields
          if (fieldName && cdeUnit.serialNumber && (
            fieldName.includes('serial') || 
            fieldName.includes('scanner') ||
            fieldName.includes('serial_no') ||
            fieldName.includes('serialnumber') ||
            fieldName.includes('serial_number') ||
            fieldName.includes('serialno') ||
            fieldName.includes('manuf') ||
            fieldName.includes('manufacturer') ||
            fieldName.includes('equipment_serial') ||
            fieldName.includes('equipmentserial')
          )) {
            initialData[field.fieldName] = cdeUnit.serialNumber;
          }

          // Check for manufacturer serial number fields
          if (fieldName && cdeUnit.manufacturerSerial && (
            fieldName.includes('manuf') ||
            fieldName.includes('manufacturer') ||
            fieldName.includes('manufact_serial')
          )) {
            initialData[field.fieldName] = cdeUnit.manufacturerSerial;
          }

          // Check for model fields
          if (fieldName && cdeUnit.model && fieldName.includes('model')) {
            initialData[field.fieldName] = cdeUnit.model;
          }

          // Check for CDE type fields
          if (fieldName && cdeUnit.type && fieldName.includes('cde_type')) {
            initialData[field.fieldName] = cdeUnit.type;
          }
        });
      }
      
      // Auto-populate customer data from localStorage
      const storedCustomerData = localStorage.getItem('customerData');
      if (storedCustomerData) {
        try {
          const customerData = JSON.parse(storedCustomerData);
          
          fields.forEach(field => {
            // Skip fields the current approval-matrix step has marked invisible.
            if (field.isVisible === false) return;

            // Skip fields that are not configured as API-populated
            if (!field.isApiPopulatedColumn) return;

            const fieldName = field.fieldName?.toLowerCase();

            // Map customer data to form fields
            if (fieldName && customerData.name && (
              fieldName.includes('customer_name') || fieldName.includes('owner') || fieldName.includes('outlet')
            )) {
              initialData[field.fieldName] = customerData.name;
            }

            if (fieldName && customerData.code && (
              fieldName.includes('customer_number') ||
              fieldName.includes('customer_no') ||
              fieldName.includes('cust_number') ||
              fieldName.includes('cust_no') ||
              fieldName.includes('customernumber') ||
              fieldName.includes('cdesc_customer_number') ||
              fieldName.includes('cdesc_customer_no')
            )) {
              initialData[field.fieldName] = customerData.code;
            }

            if (fieldName && customerData.address && fieldName.includes('address')) {
              initialData[field.fieldName] = customerData.address;
            }
          });
        } catch (error) {
          console.error('Error parsing customer data for auto-binding:', error);
        }
      }
      
      // Update form data with auto-populated values
      setFormData(initialData);

      // Trigger dependent field searches after auto-binding customer data
      await triggerDependentFieldSearches(initialData, fields);

      // Trigger onChange APIs for customer number and serial number fields after auto-binding
      setTimeout(() => {
        triggerOnChangeAPIsAfterLoad(initialData, fields);
      }, 500);
      
      // If we have a serial number, fetch equipment details
      if (scannedSerialNumber || cdeUnit?.serialNumber) {
        const serialToFetch = scannedSerialNumber || cdeUnit?.serialNumber;
        if (serialToFetch) {
          await fetchEquipmentDetails(serialToFetch);
        }
      }
      
    } catch (error) {
      console.error('Error in auto-binding:', error);
    }
  };

  const checkAndPrefillData = async (serialNumber: string, fields: FormField[]): Promise<any> => {
    console.log(`🔍 Attempting to fetch pre-fill data for serial number: ${serialNumber}`);
    try {
      const equipmentData = await equipmentService.getCustomerDetailsBySerialNumber(serialNumber);
      
      const prefillData: any = {};
      
      // Always ensure the serial number field is filled with what was searched
      const serialField = fields.find(f => f.fieldName.includes('serial') && !f.fieldName.includes('man_'));
      if (serialField) {
        prefillData[serialField.fieldName] = serialNumber;
      }

      if (equipmentData) {
        console.log('✅ Found equipment data for pre-fill:', equipmentData);
        
        // Define mapping between equipment service response and form field IDs
        // Mapping: Form_Field_ID -> Possible_API_Response_Keys
        const mapping: { [key: string]: string[] } = {
          // Equipment Info
          'serial_no_greentag': ['serialNumber', 'serial_number'],
          'man_serial': ['manufacturerSerialNumber', 'manuf_serial_no', 'manufserial_no'],
          'model_no': ['modelNumber', 'model_no'],
          'object_type': ['equipmentType', 'cde_type'],
          
          // Customer Info
          'cust_num': ['customerNumber', 'customer_number'],
          'store_name': ['customerName', 'outlet_store_name', 'outletstore_name'],
          'owner_name': ['ownerName', 'owners_name'],
          'owner_name_outlet_representative': ['ownerName', 'owners_name'],
          'outlet_address': ['customerAddress', 'outlet_address', 'address'],
          'city': ['customerCity', 'city', 'cityprovince'],
          'province': ['customerProvince', 'province'],
          'postal_code': ['customerPostalCode', 'postal_code'],
          'contact_no_one': ['customerMobile', 'contact_number1', 'contact_no_one']
        };

        // Map equipment data to form fields
        fields.forEach(field => {
          const apiKeys = mapping[field.fieldName];
          if (apiKeys) {
            // Find the first key that has a value in the equipment data
            for (const key of apiKeys) {
              const value = equipmentData[key];
              if (value !== undefined && value !== null && value !== '') {
                prefillData[field.fieldName] = value;
                break; // Move to next field once a value is found
              }
            }
          }
        });

        console.log('📝 Mapped pre-fill data:', prefillData);
      }
      
      return prefillData;
    } catch (error) {
      console.error('Error in checkAndPrefillData:', error);
      return {};
    }
  };

  const loadFormConfiguration = async (formName: string) => {
    setIsLoading(true);
    try {
      // Use Web API service for form configuration
      const response = await webApiFormService.getFormConfiguration(formName);

      console.log('📋 Form Configuration Response:', response);

      if (!response.success) {
        throw new Error(response.message || 'Failed to get form configuration');
      }

      // For verification form (public registration), bypass access check
      const isPublicForm = formName === 'CDE Verification using Serial Number';
      
      if (!response.canCreate && !isPublicForm) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      if (!response.data || response.data.length === 0) {
        throw new Error('No form configuration available');
      }

      // Convert Web API response format to FormConfiguration format
      const formConfig = await convertWebApiResponseToFormConfig(response);

      // Hide form number field for Customer Unit Registration and Verification forms
      const shouldHideFormNumber = formName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION ||
                                    formName === FORM_NAMES.VERIFICATION;
      if (shouldHideFormNumber) {
        const headerTableName = response.tableNames && 'header' in response.tableNames ? response.tableNames.header : undefined;
        if (headerTableName && typeof headerTableName === 'string') {
          const numberFieldName = headerTableName + '_number';
          formConfig.fields = formConfig.fields.map((field: FormField) => {
            if (field.fieldName === numberFieldName) {
              console.log(`🚫 Hiding form number field: ${numberFieldName}`);
              return { ...field, isVisible: false };
            }
            return field;
          });
        }
      }

      setFormConfig(formConfig);

      // For public forms, ensure access is granted
      if (isPublicForm) {
        setHasAccess(true);
      }

      // Initialize form data with default values
      const initialData: any = {};

      formConfig.fields.forEach((field: FormField) => {
        if (field.defaultValue !== undefined && field.defaultValue !== '') {
          initialData[field.fieldName] = field.defaultValue;
        }
        // Set today's date as default for date and datetime fields — but only if visible
        // (Per ApprovalMatrixLineFormConfiguration.UI_Field_IsVisible: invisible fields shouldn't be auto-captured)
        else if (field.isVisible && (field.fieldType === 'date' || field.fieldType === 'datetime' || field.fieldType === 'datetime-local')) {
          const today = new Date();
          if (field.fieldType === 'date') {
            // Format as YYYY-MM-DD for date input
            initialData[field.fieldName] = today.toISOString().split('T')[0];
          } else {
            // Format as YYYY-MM-DDTHH:MM for datetime-local input
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            const hours = String(today.getHours()).padStart(2, '0');
            const minutes = String(today.getMinutes()).padStart(2, '0');
            initialData[field.fieldName] = `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        }
      });

      // Check for pre-filled serial number from navigation state
      if (location.state && location.state.prefillSerialNumber) {
        const prefillData = await checkAndPrefillData(location.state.prefillSerialNumber, formConfig.fields);
        // Merge prefillData into initialData before setting state
        Object.assign(initialData, prefillData);
      }

      // CRITICAL: Do NOT generate form number on frontend
      // The backend will generate the proper sequence number when UI_Input_Value = 'backend'
      // This matches Angular behavior where 'backend' fields return null and let the server handle it
      const headerTableName = response.tableNames && 'header' in response.tableNames ? response.tableNames.header : undefined;
      if (headerTableName && typeof headerTableName === 'string') {
        const numberFieldName = headerTableName + '_number';

        // If the form number field has 'backend' as its value, clear it so backend can generate
        if (initialData[numberFieldName] === 'backend') {
          delete initialData[numberFieldName];
          console.log(`🔢 Form number field ${numberFieldName} will be generated by backend`);
        }
      }

      setFormData(initialData);

      // Skip auto-binding for Customer Unit Registration form (should always be blank)
      const skipAutoBinding = formName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION;

      if (!skipAutoBinding) {
        // Auto-bind scanner and customer data from navigation flow
        await autoBindScannerAndCustomerData(initialData, formConfig.fields);

        // Load customer data after form is initialized (skip for public verification forms)
        if (!isPublicForm) {
          loadCustomerData(initialData, formConfig.fields);
        }
      } else {
        console.log('⏭️ Skipping auto-binding for Customer Unit Registration form');
      }
    } catch (error: any) {
      console.error('Error loading form configuration:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Configuration Error',
        message: error.message || 'Failed to load form configuration. Please try again later.',
        onConfirm: () => navigate('/dashboard')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCustomerData = (existingFormData?: any, fields?: FormField[]) => {
    const storedCustomerData = localStorage.getItem('customerData');
    const greenTag = localStorage.getItem('greenTagNumber');
    const userCode = localStorage.getItem('userCode');

    if (storedCustomerData) {
      try {
        const customerData = JSON.parse(storedCustomerData);

        // Create a map of possible field name variations for flexible matching
        const fieldVariations: Record<string, string[]> = {
          owner: ['owner_name', 'owners_name', 'owner_nm', 'ownername'],
          outlet: ['outlet_store', 'outlet_store_name', 'store_name', 'aliasname', 'alias_name'],
          customer_number: ['customer_number', 'customer_no', 'cust_no', 'cust_number', 'customernumber'],
          customer_name: ['customer_name', 'cust_name', 'customername'],
          customer_mobile: ['customer_mobile', 'mobile', 'phone', 'contact', 'mobile_no'],
          customer_email: ['customer_email', 'email', 'email_address'],
          address: ['address', 'customer_address', 'outlet_address', 'cust_address'],
          city: ['city', 'customer_city', 'cust_city'],
          province: ['province', 'customer_province', 'state'],
          postal_code: ['postal_code', 'postalcode', 'zip_code', 'zipcode'],
          green_tag: ['green_tag', 'green_tag_number', 'greentag', 'tag_number'],
          route_number: ['route_number', 'route_no', 'route'],
          sales_region: ['sales_region', 'region', 'sales_area'],
          user_code: ['user_code', 'usercode', 'user']
        };

        // Build field mapping dynamically based on actual form fields
        const customerFieldMapping: Record<string, any> = {};

        if (fields && fields.length > 0) {
          fields.forEach((field: FormField) => {
            const fieldNameLower = field.fieldName.toLowerCase();

            // CRITICAL: Skip camera, image, and file upload fields - don't auto-populate them
            const isImageOrCameraField = field.fieldType === 'camera' ||
                                         field.fieldType === 'fileupload' ||
                                         field.fieldType === 'image' ||
                                         field.fieldType === 'file';

            if (isImageOrCameraField) {
              return; // Skip this field
            }

            // CRITICAL: Skip "Reason", "Instruction", and "Comment" fields from auto-population
            // This prevents customer data (like name/address) from being put into reason/instruction fields
            const isReasonOrInstructionField = 
              fieldNameLower.includes('reason') || 
              fieldNameLower.includes('instruction') || 
              fieldNameLower.includes('comment') ||
              fieldNameLower.includes('remarks') ||
              fieldNameLower.includes('description');

            if (isReasonOrInstructionField) {
              return; // Skip these fields
            }

            // Skip fields the current approval-matrix step has marked invisible.
            // Visibility is per-step (ApprovalMatrixLineFormConfiguration.UI_Field_IsVisible);
            // auto-populating an invisible field silently persists data the user
            // never saw — same bug class as the init-default gate at line ~1076.
            if (field.isVisible === false) {
              return;
            }

            // Check for owner field
            if (fieldVariations.owner.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.name || customerData.Name || '';
            }
            // Check for outlet/store field
            else if (fieldVariations.outlet.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.aliasName || customerData.AliasName || customerData.name || '';
            }
            // Check for customer number field
            else if (fieldVariations.customer_number.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.code || customerData.Code || customerData.number || '';
            }
            // Check for customer name field
            else if (fieldVariations.customer_name.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.name || customerData.Name || '';
            }
            // Check for mobile/phone field
            else if (fieldVariations.customer_mobile.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.mobile || customerData.Mobile || customerData.phone || '';
            }
            // Check for email field
            else if (fieldVariations.customer_email.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.email || customerData.Email || '';
            }
            // Check for address field
            else if (fieldVariations.address.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.address || customerData.Address || customerData.Line1 || '';
            }
            // Check for city field
            else if (fieldVariations.city.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.city || customerData.City || '';
            }
            // Check for province field
            else if (fieldVariations.province.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.province || customerData.Province || '';
            }
            // Check for postal code field
            else if (fieldVariations.postal_code.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.postalCode || customerData.PostalCode || customerData.zipCode || '';
            }
            // Check for green tag field
            else if (fieldVariations.green_tag.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = greenTag || '';
            }
            // Check for route number field
            else if (fieldVariations.route_number.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.routeNumber || customerData.RouteNumber || '';
            }
            // Check for sales region field
            else if (fieldVariations.sales_region.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = customerData.salesRegion || customerData.SalesRegion || '';
            }
            // Check for user code field
            else if (fieldVariations.user_code.some(v => fieldNameLower.includes(v))) {
              customerFieldMapping[field.fieldName] = userCode || '';
            }
            // Check for date/time request field
            else if (fieldNameLower.includes('date_time_request') || fieldNameLower.includes('request_date')) {
              customerFieldMapping[field.fieldName] = new Date().toISOString().slice(0, 16);
            }
          });
        }

        // Update form data with all mapped fields
        setFormData((prev: any) => {
          // Use existing form data or previous state
          const baseData = existingFormData || prev;
          const updated = { ...baseData };
          Object.entries(customerFieldMapping).forEach(([field, value]) => {
            if (value) { // Only set if value exists
              updated[field] = value;
            }
          });
          return updated;
        });

      } catch (error) {
        console.error('Error loading customer data:', error);
      }
    }
  };

  const handleFieldChange = async (fieldName: string, value: any) => {
    if (fieldName === '_repeatingSectionData') {
      setRepeatingSectionData(value || {});
      return;
    }

    setFormData((prev: any) => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    // Check if phone number changed for Customer Unit Registration form (reset OTP validation)
    if (currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION &&
        (fieldName === 'contact_no_one' || fieldName.toLowerCase().includes('contact_no')) &&
        otpValidated &&
        value !== validatedPhoneNumber) {
      console.log('📞 Phone number changed from', validatedPhoneNumber, 'to', value);
      console.log('🔄 Resetting OTP validation status');
      setOtpValidated(false);
      setValidatedPhoneNumber('');
    }

    // Handle special fields that trigger equipment fetch
    // IMPORTANT: Skip ALL serial number fields as they're handled by DynamicFormRenderer's own binding
    const isSerialNumberField =
      fieldName === 'Serial_Number' ||
      fieldName === 'serial_no_scanner' ||
      fieldName === 'Serial_No_Scanner' ||
      fieldName === 'cdesc_serial_no' ||
      fieldName.toLowerCase().includes('serial') ||
      fieldName.toLowerCase().includes('equipmentmaster');

    if (isSerialNumberField) {
      // Do nothing - all serial number binding is handled by DynamicFormRenderer
      return;
    }

    // IMPORTANT: Do NOT handle any serial number fields at all in DynamicServiceRequest
    // All serial number binding should be handled by DynamicFormRenderer
    // Comment out the equipment fetch logic completely
    /*
    if ((fieldName === 'Serial_Number' ||
         fieldName === 'serial_no_scanner' ||
         fieldName === 'Serial_No_Scanner' ||
         fieldName === 'cdesc_serial_no') && value && value.trim()) {
      console.log(`Triggering fetchEquipmentDetails for field: ${fieldName} with value: ${value}`);
      await fetchEquipmentDetails(value.trim());
    }
    */
  };

  const fetchEquipmentDetails = async (serialNo: string) => {
    if (!serialNo || serialNo.trim() === '') return;

    try {

      // Try using dynamic entity service first
      const greenTag = localStorage.getItem('greenTagNumber') || '1234';
      const response = await dynamicEntityService.getEntityData(
        'Equipment',
        greenTag,
        {
          serial_number: serialNo
        }
      );

      if (response.success) {
        let equipmentData: any = null;

        // Handle different response formats
        if (response.data) {
          if (Array.isArray(response.data) && response.data.length > 0) {
            equipmentData = response.data[0];
          } else if (typeof response.data === 'object' && !Array.isArray(response.data)) {
            equipmentData = response.data;
          }
        }

        if (!equipmentData && response.metadata && response.metadata.equipmentData) {
          equipmentData = response.metadata.equipmentData;
        }

        if (equipmentData) {
          console.log('Equipment data found:', equipmentData);

          // Update form data with equipment details
          setFormData((prev: any) => ({
            ...prev,
            Manufacturer_Serial_Number: equipmentData.manuf_serial_no || equipmentData.ManufacturerSerialNumber || equipmentData.manufSerialNo || prev.Manufacturer_Serial_Number || '',
            Model_Number: equipmentData.model_no || equipmentData.CDEModel || equipmentData.modelNo || equipmentData.modelNumber || prev.Model_Number || '',
            CDE_Type: equipmentData.cde_type || equipmentData.CDEType || equipmentData.equipmentType || prev.CDE_Type || '',
            Customer_Number: equipmentData.customer_number || equipmentData.CustomerNumber || equipmentData.customerNumber || prev.Customer_Number || '',
            Outlet_Store_Name: equipmentData.outlet_store_name || equipmentData.CustomerName || equipmentData.customerName || equipmentData.aliasName || prev.Outlet_Store_Name || '',
            Owner_Name: equipmentData.owners_name || equipmentData.CustomerName || equipmentData.ownerName || equipmentData.customerName || prev.Owner_Name || '',
            Outlet_Address: equipmentData.customer_address || equipmentData.CustomerAddress || equipmentData.customerAddress || prev.Outlet_Address || ''
          }));

          setAlert({
            isVisible: true,
            type: 'success',
            title: 'Equipment Found',
            message: `Equipment details loaded for ${serialNo}`
          });
          return;
        }
      }

      // If dynamic entity service didn't work, try equipment service
      const customerDetails = await equipmentService.getCustomerDetailsBySerialNumber(serialNo);

      if (customerDetails) {
        setFormData((prev: any) => ({
          ...prev,
          Manufacturer_Serial_Number: customerDetails.manufSerialNo || customerDetails.manufacturerSerialNumber || prev.Manufacturer_Serial_Number,
          Model_Number: customerDetails.modelNo || customerDetails.modelNumber || prev.Model_Number,
          CDE_Type: customerDetails.equipmentType || prev.CDE_Type,
          Customer_Number: customerDetails.customerNumber || prev.Customer_Number,
          Outlet_Store_Name: customerDetails.customerName || customerDetails.aliasName || prev.Outlet_Store_Name,
          Owner_Name: customerDetails.ownerName || customerDetails.customerName || prev.Owner_Name,
          Outlet_Address: customerDetails.customerAddress || prev.Outlet_Address
        }));

        // Success - no alert needed, data is auto-populated
      } else {
        console.log('No equipment found for serial number:', serialNo);
        setAlert({
          isVisible: true,
          type: 'warning',
          title: 'Not Found',
          message: `No equipment found for serial number: ${serialNo}`
        });
      }
    } catch (error) {
      console.error('Error fetching equipment details:', error);
      setAlert({
        isVisible: true,
        type: 'warning',
        title: 'Fetch Failed',
        message: 'Could not fetch equipment details. Please enter manually.'
      });
    }
  };

  const handleScan = async (fieldName: string, value: string) => {
    console.log('📷 QR Scanner - Field scanned:', fieldName, 'Value:', value);

    // Store serial number in localStorage for persistence
    if (value && value.trim()) {
      localStorage.setItem('serialNumber', value.trim());
      console.log('📦 Stored scanned serial number in localStorage:', value.trim());
    }

    // Note: DynamicFormRenderer already triggers the API calls automatically
    // when the field has onChangeApiEndpoint1 configured, so we don't need
    // to manually trigger them here. This prevents duplicate API calls.

    setAlert({
      isVisible: true,
      type: 'success',
      title: 'Scanned',
      message: `Barcode scanned: ${value}`
    });
  };

  const handleLocationCapture = (latitude: number, longitude: number) => {
    setFormData((prev: any) => ({
      ...prev,
      Latitude: latitude.toString(),
      Longitude: longitude.toString(),
      GPS_Location: `${latitude},${longitude}`
    }));

    setAlert({
      isVisible: true,
      type: 'success',
      title: 'Location Captured',
      message: 'Your current location has been captured successfully.'
    });
  };

  // Helper function to check if a field is dynamically visible (considering parent field conditions)
  const isFieldDynamicallyVisible = (field: FormField): boolean => {
    // Hide specific fields that should not be displayed or validated in the form
    const hiddenFieldIdentifiers = [
      'cv_mn_wk_ctr',
      'cv_sys_status',
      'cv_cde_assigne',
      'cv_tsm',
      'cv_route_number',
      'cv_channel',
      'cv_segmentation',
      'cv_in_scl',
      'cv_customer_cde_record_qty',
      'cv_ave_monthly_volume',
      'cv_size_dimension',
      'cv_sort_fld',
      'cv_technical_ident_no',
      'cv_sloc',
      'cv_pp',
      'cv_physical_status',
      'with_missing_cde_record',
      'opru_with_missing_cde_record'
    ];

    const normalizedFieldName = field.fieldName.toLowerCase();
    const shouldHideField = hiddenFieldIdentifiers.some(identifier => {
      const normalizedIdentifier = identifier.toLowerCase();

      if (normalizedFieldName.includes(normalizedIdentifier)) {
        return true;
      }

      if (normalizedIdentifier.startsWith('cv_')) {
        const baseIdentifier = normalizedIdentifier.slice(3);
        if (baseIdentifier && normalizedFieldName.includes(baseIdentifier)) {
          return true;
        }
      }

      return false;
    });

    if (shouldHideField) {
      return false;
    }

    // First check static visibility
    if (!field.isVisible) return false;

    // Check parent field visibility conditions
    if (field.parentFieldId && field.parentFieldValue !== undefined) {
      const parentValue = formData[field.parentFieldId];

      // Helper to compare values with operator support
      const compareValues = (actualValue: any, expectedValue: any, operator: string = 'equals'): boolean => {
        const actual = String(actualValue || '').toLowerCase().trim();
        const expected = String(expectedValue || '').toLowerCase().trim();

        switch (operator) {
          case 'equals':
            return actual === expected;
          case 'notequals':
            return actual !== expected;
          case 'contains':
            return actual.includes(expected);
          default:
            return actual === expected;
        }
      };

      // Check primary condition
      const primaryCondition = compareValues(
        parentValue,
        field.parentFieldValue,
        field.conditionOperator || 'equals'
      );

      // Check secondary condition if exists
      if (field.secondaryParentFieldId && field.secondaryParentFieldValue !== undefined) {
        const secondaryParentValue = formData[field.secondaryParentFieldId];
        const secondaryCondition = compareValues(
          secondaryParentValue,
          field.secondaryParentFieldValue,
          field.conditionOperator || 'equals'
        );

        // Apply logical operator (AND/OR)
        if (field.conditionalLogicOperator === 'OR') {
          return primaryCondition || secondaryCondition;
        } else {
          // Default to AND
          return primaryCondition && secondaryCondition;
        }
      }

      return primaryCondition;
    }

    return true;
  };

  const validateForm = (): boolean => {
    if (!formConfig) return false;

    const newErrors: Record<string, string> = {};
    let isValid = true;

    console.log('🔍 Starting form validation for:', currentFormName);

    formConfig.fields.forEach(field => {
      // Check if field is dynamically visible (considering parent field conditions)
      const isDynamicallyVisible = isFieldDynamicallyVisible(field);

      if (isDynamicallyVisible && !field.isReadOnly) {
        const validation = formConfigService.validateField(field, formData[field.fieldName]);
        if (!validation.isValid) {
          console.log(`❌ Validation failed for field: ${field.fieldName} (${field.label})`, {
            value: formData[field.fieldName],
            error: validation.errorMessage
          });
          newErrors[field.fieldName] = validation.errorMessage || `${field.label} is invalid`;
          isValid = false;
        } else {
          console.log(`✅ Validation passed for field: ${field.fieldName} (${field.label})`);
        }
      } else if (!isDynamicallyVisible) {
        console.log(`⏭️ Skipping validation for hidden field: ${field.fieldName} (${field.label})`);
      }
    });

    if (!isValid) {
      console.log('❌ Form validation failed. Errors:', newErrors);
    } else {
      console.log('✅ Form validation passed');
    }

    setErrors(newErrors);
    return isValid;
  };

  // Revert button handler
  const handleRevertClick = async () => {
    if (!recordUid) {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: 'No record UID found for revert operation'
      });
      return;
    }

    // Load form history
    setIsLoadingHistory(true);
    try {
      const historyResponse = await webApiFormService.getFormHistory(recordUid);
      
      if (historyResponse.success && historyResponse.data && historyResponse.data.length > 0) {
        setFormHistory(historyResponse.data);
        setIsRevertModalOpen(true);
      } else {
        setAlert({
          isVisible: true,
          type: 'warning',
          title: 'No History',
          message: 'No form history available for revert'
        });
      }
    } catch (error: any) {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to load form history'
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Handle revert confirmation
  const handleRevertConfirm = async (historyUID: string, remarks: string, revertToStatus: string) => {
    setIsSubmitting(true);
    setIsRevertModalOpen(false);

    try {
      // Get the actual table name from form configuration (header table)
      const headerField = formConfig?.fields.find((f: FormField) =>
        f.tableName && f.tableType === 'header'
      );
      const actualTableName = headerField?.tableName || currentFormType;

      // Wrap flat form data in table structure (matching Angular's buildSubmissionData approach)
      const wrappedFormData: any = {};
      wrappedFormData[actualTableName] = {
        ...formData,
        status: revertToStatus,
        uid: recordUid
      };

      // Call submitDynamicForm directly (like Angular does) - bypass submitFormWithImages
      const result = await webApiFormService.submitDynamicForm(
        currentFormName,
        wrappedFormData,
        {
          nextStatus: revertToStatus,
          approvalMatrixLineUID: approvalMatrixLineUID, // Use stored UID from current status
          isReverted: true, // CRITICAL: Mark this as a revert operation
          selectedRevertHistoryUID: historyUID, // CRITICAL: Pass the selected history UID
          remarks,
          mode: 'EDIT',
          uid: recordUid
        }
      );

      if (result.success) {
        setAlert({
          isVisible: true,
          type: 'success',
          title: 'Reverted',
          message: `Form reverted to ${revertToStatus} status successfully!`
        });

        // Auto-redirect to dashboard after 2 seconds (matching Angular behavior)
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        throw new Error(result.message || 'Failed to revert form');
      }
    } catch (error: any) {
      console.error('❌ Revert error:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Revert Failed',
        message: error.message || 'Failed to revert form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPSuccess = () => {
    console.log('✅ OTP Verified successfully');

    // Store the validated phone number to prevent showing OTP again unless phone changes
    const validatedPhone = pendingSubmissionData?.contactNumber || '';

    setOtpValidated(true);
    setValidatedPhoneNumber(validatedPhone);
    setShowOTPModal(false);
    setIsSubmitting(false);

    // Instead of calling handleSubmit again (which would check OTP status before state updates),
    // directly proceed with form submission by calling submitAfterOTPValidation
    setTimeout(() => {
      submitAfterOTPValidation();
    }, 300);
  };

  const submitAfterOTPValidation = async () => {
    // Clear pending data
    setPendingSubmissionData(null);

    // Continue with normal form submission (will use PublicDynamicForm endpoint)
    await continueFormSubmission();
  };

  const continueFormSubmission = async () => {
    setIsSubmitting(true);
    try {
      // Transform form data for submission
      const transformedData = formConfigService.transformFormDataForSubmission(
        formData,
        formConfig!.fields
      );

      // Ensure phone/mobile fields are saved as strings
      formConfig!.fields.forEach(field => {
        const fieldNameLower = field.fieldName.toLowerCase();
        if ((fieldNameLower.includes('phone') || fieldNameLower.includes('mobile') ||
             fieldNameLower.includes('contact') || fieldNameLower.includes('tel')) &&
            transformedData[field.fieldName] !== undefined && transformedData[field.fieldName] !== null) {
          // Convert to string to preserve full number
          transformedData[field.fieldName] = String(transformedData[field.fieldName]);
        }
      });

      // Convert image files to base64 strings
      const imageFields = formConfig!.fields.filter(f =>
        f.fieldType === 'fileupload' ||
        f.fieldType === 'image' ||
        f.fieldType === 'Image Uploader' ||
        f.fieldType === 'camera'
      );

      // Helper function to check if a field is visible based on parent conditions
      const isFieldCurrentlyVisible = (field: FormField): boolean => {
        if (!field.isVisible) return false;

        // Check parent field visibility conditions
        if (field.parentFieldId && field.parentFieldValue !== undefined && field.parentFieldValue !== null && field.parentFieldValue !== '') {
          const parentValue = formData[field.parentFieldId];
          const operator = field.conditionOperator || 'equals';

          // Compare values
          const compareValues = (actual: any, expected: any, op: string): boolean => {
            const actualStr = String(actual || '').toLowerCase().trim();
            const expectedStr = String(expected || '').toLowerCase().trim();

            switch (op) {
              case 'equals': return actualStr === expectedStr;
              case 'not_equals': return actualStr !== expectedStr;
              case 'contains': return actualStr.includes(expectedStr);
              default: return actualStr === expectedStr;
            }
          };

          const primaryConditionMet = compareValues(parentValue, field.parentFieldValue, operator);

          // Check secondary condition if exists
          if (field.secondaryParentFieldId && field.secondaryParentFieldValue !== undefined) {
            const secondaryParentValue = formData[field.secondaryParentFieldId];
            const secondaryConditionMet = compareValues(secondaryParentValue, field.secondaryParentFieldValue, 'equals');

            if (field.conditionalLogicOperator === 'AND' || !field.conditionalLogicOperator) {
              return primaryConditionMet && secondaryConditionMet;
            } else {
              return primaryConditionMet || secondaryConditionMet;
            }
          }

          return primaryConditionMet;
        }

        return true;
      };

      const submissionData = { ...transformedData };

      if (Object.keys(repeatingSectionData).length > 0) {
        submissionData._repeatingSectionData = repeatingSectionData;
      }

      // Helper function to convert file to base64
      const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      // Convert File objects to base64 strings for image fields
      let hasImageFieldsWithoutData = false;
      for (const field of imageFields) {
        const fieldValue = formData[field.fieldName];

        // Check if the field is currently visible
        const fieldIsVisible = isFieldCurrentlyVisible(field);

        // Check if mandatory image field has no data
        // Only validate if the field is mandatory AND currently visible
        const hasValidData = fieldValue && (
          (Array.isArray(fieldValue) && fieldValue.length > 0) ||
          (fieldValue instanceof File) ||
          (typeof fieldValue === 'string' && fieldValue.length > 0)
        );

        if (field.isMandatory && fieldIsVisible && !hasValidData) {
          hasImageFieldsWithoutData = true;
          console.log(`❌ Mandatory visible image field "${field.fieldName}" has no valid data`);
          continue;
        }

        // Skip processing hidden fields
        if (!fieldIsVisible) {
          continue;
        }

        // Process the field value based on its type
        if (fieldValue) {
          // Already a base64 string - use directly
          if (typeof fieldValue === 'string' && fieldValue.startsWith('data:')) {
            submissionData[field.fieldName] = fieldValue;
          }
          // Already a path string (from edit mode) - use directly
          else if (typeof fieldValue === 'string' && fieldValue.length > 0) {
            submissionData[field.fieldName] = fieldValue;
          }
          // Array of files or mixed (File objects and base64 strings)
          else if (Array.isArray(fieldValue) && fieldValue.length > 0) {
            try {
              const base64Promises = fieldValue.map(async (item: File | string) => {
                // Already base64 or path string
                if (typeof item === 'string') {
                  return item;
                }
                // File object - convert to base64
                if (item instanceof File) {
                  return await convertFileToBase64(item);
                }
                return '';
              });

              const base64Results = await Promise.all(base64Promises);
              // Filter out empty strings and join with pipe separator
              const validResults = base64Results.filter(r => r);
              if (validResults.length > 0) {
                submissionData[field.fieldName] = validResults.join('|');
              }
            } catch (error) {
              console.error(`Error converting images to base64 for ${field.fieldName}:`, error);
              delete submissionData[field.fieldName];
            }
          }
          // Single File object
          else if (fieldValue instanceof File) {
            try {
              submissionData[field.fieldName] = await convertFileToBase64(fieldValue);
            } catch (error) {
              console.error(`Error converting image to base64 for ${field.fieldName}:`, error);
              delete submissionData[field.fieldName];
            }
          }
        }
      }
      
      // Prevent submission if mandatory image fields have no data
      if (hasImageFieldsWithoutData) {
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Missing Images',
          message: 'Please upload all required images before submitting the form.'
        });
        setIsSubmitting(false);
        return;
      }
      
      // In edit mode, include the UID
      if (mode === 'edit' && recordUid) {
        submissionData.uid = recordUid;
        console.log(`✅ EDIT MODE: Added UID to submission data:`, recordUid);
      } else if (mode === 'edit') {
        console.error('❌ EDIT MODE: No recordUid available!');
      } else {
        console.log('🆕 ADD MODE: No UID needed');
      }

      // Get form configuration with table names and approval matrix from Web API
      let tableNames: { header?: string; detail?: string } = {};
      let approvalMatrixLineUID = '';
      const isEditMode = mode === 'edit';
      // CRITICAL FIX: For ADD mode, don't pass CurrentStatus - the approval matrix
      // expects empty string for new forms to get the initial ApprovalMatrixLineUID
      // Only pass CurrentStatus in EDIT mode where we have an existing record status
      const submittedCurrentStatus = isEditMode
        ? (formData.status || formData.Status || searchParams.get('status') || '')
        : ''; // Empty for ADD mode - this gets the correct ApprovalMatrixLineUID
      let resolvedNextStatus = nextStatus || initialNextStatus || 'Confirm';
      try {
        const configResponse = await webApiFormService.getFormConfiguration(
          currentFormName,
          isEditMode ? 'EDIT' : 'ADD',
          submittedCurrentStatus
        );
        if (configResponse.success) {
          if (configResponse.tableNames) {
            tableNames = configResponse.tableNames;
          }
          if (configResponse.approvalMatrixLineUID) {
            approvalMatrixLineUID = configResponse.approvalMatrixLineUID;
          }
          if (configResponse.nextStatus) {
            resolvedNextStatus = configResponse.nextStatus;
            console.log('✅ Using NextStatus from config:', resolvedNextStatus);
            setNextStatus(configResponse.nextStatus);
            setInitialNextStatus(configResponse.nextStatus);
          } else {
            console.warn('⚠️ No NextStatus in config, using fallback:', resolvedNextStatus);
          }
        }
      } catch (err) {
        console.warn('Could not fetch config from API, using defaults');
      }

      console.log('📦 DynamicForm request payload preview:', {
        formName: currentFormName,
        formType: currentFormType,
        nextStatus: resolvedNextStatus,
        approvalMatrixLineUID,
        tableNames,
        payload: submissionData
      });

      // Check if this is a public form (Customer Unit Registration or Verification)
      const isPublicForm = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION ||
                           currentFormName === FORM_NAMES.VERIFICATION;

      // Use Web API service to submit form (images already converted to base64 in submissionData)
      const response = await webApiFormService.submitFormWithImages(
        currentFormName,
        currentFormType,
        submissionData,
        undefined, // No separate image files - they're already base64 in submissionData
        {
          nextStatus: resolvedNextStatus,
          remarks: formData.remarks || formData.Remarks || '',
          tableNames: tableNames,
          approvalMatrixLineUID: approvalMatrixLineUID,
          mode: isEditMode ? 'EDIT' : 'ADD',
          currentStatus: submittedCurrentStatus,
          isPublicForm: isPublicForm
        }
      );

      console.log('📝 Full Submission Response:', response);

      if (response.success) {
      // Determine if we should redirect only on click (manual) or keep the timeout (automatic)
      // User requested manual redirect specifically for Raise Service Request and Registration
      const isRaiseServiceRequest = location.pathname === '/raise-service-request';
      const isRegistration = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION;

      // Extract serial number from form data for display in message
      const serialNumber = formData.Serial_Number || formData.serial_no_scanner || formData.Serial_No_Scanner || formData.cdesc_serial_no || 
        Object.entries(formData).find(([key]) => key.toLowerCase().includes('serial'))?.[1] as string || '';

      // Extract CDE Type for conditional messages
      const cdeTypeField = Object.entries(formData).find(([key]) => {
        const lowerKey = key.toLowerCase();
        return lowerKey === 'cde_type' || 
               lowerKey === 'cdesc_cde_type' || 
               lowerKey === 'object_type' ||
               lowerKey === 'equipment_type' ||
               lowerKey.includes('cde_type');
      });
      const cdeType = (cdeTypeField?.[1] as string || '').toLowerCase();

      // Custom messages: For Raise Service Request and Registration, we override the API message to show form number
      let successMessage = '';
      // Use type assertion to handle the union type and potential extra properties from API
      const resp = response as any;

      // Look for the Form/Ticket Number in all possible locations in the response
      // Check direct properties and nested data properties
      let ticketNumber = resp.referenceNumber || 
                         resp.ReferenceNumber || 
                         resp.formNumber || 
                         resp.form_number ||
                         (resp.data && (
                           resp.data.ReferenceNumber || 
                           resp.data.referenceNumber || 
                           resp.data.ReferenceNo ||
                           resp.data.form_number || 
                           resp.data.Form_Number ||
                           resp.data.FormNumber ||
                           resp.data.TicketNumber
                         ));

      // If still not found, try to find any string in the response that looks like a ticket number (e.g., starts with CSC)
      if (!ticketNumber && resp.data) {
        const values = Object.values(resp.data);
        const foundId = values.find(v => typeof v === 'string' && (v.startsWith('CSC') || v.startsWith('SR')));
        if (foundId) ticketNumber = foundId;
      }

      // Final fallback
      ticketNumber = ticketNumber || 'N/A';

      if (isRegistration) {
        successMessage = `Registered successfully!\nSerial: ${serialNumber}`;
      } else if (isRaiseServiceRequest) {
        if (cdeType.includes('fountain')) {
          successMessage = `Service Request submitted successfully.\nExpected response time: Within 24 hours\n(Requests submitted after 4 PM will be processed on the next business day.)\n\nTo track status or raise a follow-up, please visit the portal using your Green Tag or Inventory Number and check the Ticket History tab.`;
        } else if (cdeType.includes('cooler')) {
          successMessage = `Service Request submitted successfully.\nExpected response time: Within 2–3 working days.\n\nIf there is any delay, you can check the status or submit a follow-up through the portal using your Green Tag or Inventory Number under the Ticket History tab.`;
        } else {
          successMessage = `Service Request ${ticketNumber} has been successfully submitted.`;
        }
      } else {        // Fallback to API message or default for other forms
        successMessage = response.referenceNumber
          ? `Service request submitted successfully! Reference: ${response.referenceNumber}`
          : response.message || 'Service request submitted successfully!';
      }

      const navigationPath = isRegistration ? '/' : '/dashboard';

      // Show success message and navigate
      const handleRedirection = () => {
        if (isRegistration) {
          navigate('/', { state: { autoValidateSerial: serialNumber } });
        } else {
          navigate(navigationPath);
        }
      };

      if (isRaiseServiceRequest || isRegistration) {
        // Manual redirect on OK click
        setAlert({
          isVisible: true,
          type: 'success',
          title: 'Success',
          message: successMessage,
          onConfirm: handleRedirection,
          onClose: handleRedirection
        });
      } else {          // Keep legacy behavior (auto-redirect after timeout) for all other forms
          setAlert({
            isVisible: true,
            type: 'success',
            title: 'Success',
            message: successMessage
          });

          setTimeout(() => {
            handleRedirection();
          }, 1500);
        }
      } else {
        throw new Error(response.message || 'Failed to submit service request');
      }
    } catch (error: any) {
      console.error('Error submitting service request:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Submission Failed',
        message: error.message || 'Failed to submit service request. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPCancel = () => {
    setShowOTPModal(false);
    setOtpValidated(false);
    setPendingSubmissionData(null);
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Validation Error',
        message: 'Please fill in all required fields correctly.'
      });
      return;
    }

    // Check if this is Customer/Unit Registration form and OTP validation is required
    const requiresOTP = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION;

    if (requiresOTP) {
      // Find contact number field (UI_Field_Id = "contact_no_one")
      const contactField = formConfig?.fields.find(
        field => field.fieldName.toLowerCase().includes('contactno') ||
                 field.fieldName.toLowerCase().includes('contact_no') ||
                 field.fieldName === 'contact_no_one'
      );

      const contactNumber = contactField ? formData[contactField.fieldName] : null;

      if (!contactNumber) {
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Contact Number Required',
          message: 'Please enter a contact number to proceed with OTP validation.'
        });
        return;
      }

      // Check if OTP validation is needed:
      // 1. OTP not validated yet, OR
      // 2. Phone number has changed from the validated one
      const needsOTPValidation = !otpValidated || contactNumber !== validatedPhoneNumber;

      if (needsOTPValidation) {
        // Store pending submission data and show OTP modal
        setIsSubmitting(true);
        setPendingSubmissionData({ contactNumber });
        setShowOTPModal(true);
        return;
      }
    }

    // OTP not needed or already validated - proceed with submission
    await continueFormSubmission();
  };

  const handleCancel = () => {
    // For Customer Unit Registration, redirect to home page
    const redirectPath = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION ? '/' : '/dashboard';

    setAlert({
      isVisible: true,
      type: 'confirm',
      title: 'Cancel Request',
      message: 'Are you sure you want to cancel this request? All entered data will be lost.',
      onConfirm: () => navigate(redirectPath)
    });
  };

  if (isLoading) {
    const hideHeaderElements = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION;
    return (
      <PageContainer>
        <CustomerHeader skipAuthCheck={true} hideCustomerData={hideHeaderElements} showHomeButton={!hideHeaderElements} />
        <ContentContainer>
          <LoadingContainer>
            <LoadingSpinner isVisible={true} message="Loading form configuration..." />
          </LoadingContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (!hasAccess) {
    const formInfo = getFormTypeFromParams();
    const hideHeaderElements = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION;
    return (
      <PageContainer>
        <CustomerHeader skipAuthCheck={true} hideCustomerData={hideHeaderElements} showHomeButton={!hideHeaderElements} />
        <ContentContainer>
          <NoAccessContainer>
            <NoAccessIcon>
              <FaLock />
            </NoAccessIcon>
            <NoAccessTitle>Access Denied</NoAccessTitle>
            <NoAccessMessage>
              You do not have permission to create {formInfo.title}s.
              Please contact your administrator for access.
            </NoAccessMessage>
            <BackButton onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </BackButton>
          </NoAccessContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (!formConfig) {
    const hideHeaderElements = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION;
  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} hideCustomerData={hideHeaderElements} showHomeButton={!hideHeaderElements} />
      <ContentContainer>
        <Title>Configuration Error</Title>
          <p style={{ textAlign: 'center', color: 'red' }}>
            Failed to load form configuration. Please contact support.
          </p>
        </ContentContainer>
      </PageContainer>
    );
  }

  const formInfo = getFormTypeFromParams();
  const isPublicForm = formInfo.isPublic || false;

  // Extract form number from formData using header table name
  const getFormNumber = () => {
    // Find the header table name from form configuration
    const headerField = formConfig?.fields.find((f: FormField) =>
      f.tableName && f.tableType === 'header'
    );

    if (headerField && headerField.tableName) {
      const numberFieldName = headerField.tableName + '_number';
      const value = formData[numberFieldName];

      // Skip if value is empty, null, undefined, or the word "backend"
      if (value && value !== 'backend' && value !== '' && value !== null && value !== undefined) {
        return value;
      }
    }

    return null;
  };

  const formNumber = getFormNumber();
  const hideHeaderElements = currentFormName === FORM_NAMES.CUSTOMER_UNIT_REGISTRATION;

  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} hideCustomerData={hideHeaderElements} showHomeButton={!hideHeaderElements} />
      <ContentContainer>
        <Title>{formInfo.title}</Title>

        <DynamicFormRenderer
          fields={formConfig.fields}
          sections={formConfig.sections}
          formData={formData}
          onChange={handleFieldChange}
          errors={errors}
          onScan={handleScan}
          onLocationCapture={handleLocationCapture}
          autoTriggerSearchField={location.pathname === '/raise-service-request' ? 'serial_no_scanner' : undefined}
        />

        <ButtonGroup>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {mode === 'view' ? 'Back' : 'Cancel'}
          </Button>
          {canRevert && mode === 'edit' && (
            <Button
              variant="secondary"
              onClick={handleRevertClick}
              disabled={isSubmitting || isLoadingHistory}
              style={{ background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)', color: 'white' }}
            >
              {isLoadingHistory ? 'Loading...' : 'Revert'}
            </Button>
          )}
          {mode !== 'view' && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 
                (mode === 'edit' ? 'Updating...' : 'Submitting...') : 
                (mode === 'edit' ? 'Update' : 'Submit Request')
              }
            </Button>
          )}
        </ButtonGroup>

        {/* Form number display hidden for all forms */}
      </ContentContainer>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={alert.onClose || (() => setAlert({ isVisible: false, message: '' }))}
        onConfirm={alert.onConfirm}
      />

      <RevertModal
        isOpen={isRevertModalOpen}
        onClose={() => setIsRevertModalOpen(false)}
        onConfirm={handleRevertConfirm}
        formHistory={formHistory}
        isLoading={isSubmitting}
      />

      {/* OTP Validation Modal for Customer/Unit Registration */}
      {showOTPModal && pendingSubmissionData && (
        <OTPValidationModal
          isVisible={showOTPModal}
          phoneNumber={pendingSubmissionData.contactNumber}
          onSuccess={handleOTPSuccess}
          onCancel={handleOTPCancel}
          customerCode={formData.customer_number || formData.Customer_Number || localStorage.getItem('customerCode') || 'CUST001'}
        />
      )}
    </PageContainer>
  );
};

export default DynamicServiceRequest;